import React, { useEffect, useState, useRef } from 'react';
import Base from '../Base';
import Title from '../../components/common/Title';
import { useTranslation } from 'react-i18next';
import { fetchUserInfo, handleImageUpload, apiRequest, canSharePhotos, downloadPhoto, sharePhoto } from '../../utils';
import Card from '../../components/elements/Card';
import Image from '../../components/images/Image';
import Icon from '../../components/common/Icon';
import SameLine from '../../components/css/SameLine';
import Back from '../../components/common/Back';
import Center from '../../components/css/Center';
import Button from '../../components/elements/Button';
import Toast from '../../components/forms/Toast';
import Modal from '../../components/elements/Modal';

const fallbackMissionImage = require('./../../assets/images/fallback/mission.png');

const StatusPill = ({ submission }) => {
  const { t } = useTranslation();
  if (!submission) {
    return (
      <span className="status-pill status-pill--todo">
        <Icon name="circle" /> {t('missionStatusTodo')}
      </span>
    );
  }
  if (submission.is_approved) {
    return (
      <span className="status-pill status-pill--approved">
        <Icon name="check-circle" /> {t('missionStatusApproved')}
      </span>
    );
  }
  return (
    <span className="status-pill status-pill--pending">
      <Icon name="duration-alt" /> {t('missionStatusPending')}
    </span>
  );
};

const MissionPage = () => {
  const { t } = useTranslation();
  const [mission, setMission] = useState(null);
  const [submission, setSubmission] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showToast, setShowToast] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [userInfo, setUserInfo] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [savingPhoto, setSavingPhoto] = useState(false);

  // A property of the browser, not of the photograph, so it is asked once.
  const [shareable] = useState(canSharePhotos);

  // Same cross-origin story as the albums: a mission answer is a blob on the storage account, so
  // <a download> was ignored and the button only ever opened the picture. See utils.
  const savePhoto = async (action) => {
    if (savingPhoto || !selectedImage) return;
    setSavingPhoto(true);
    try {
      await action(selectedImage);
    } finally {
      setSavingPhoto(false);
    }
  };

  const cameraInputRef = useRef(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchUserInfo(
      '/mission',
      (data) => {
        setMission(data.mission);
        setSubmission(data.submission);
        setUserInfo(data);
      },
      setLoading,
      setError,
      []
    );
  }, []);

  const uploadFile = (e) => {
    const file = e.target.files[0];
    if (!file || !mission) return;
    handleImageUpload(file, '/api/submit_mission', setError, setShowToast);
  };

  const deleteSubmission = async () => {
    if (!submission || deleting) return;
    if (!window.confirm(t('confirmDeleteSubmission'))) return;

    try {
      setDeleting(true);
      setError(null);
      await apiRequest('DELETE', '/submissions/delete', {
        submission_id: submission.submission_id,
      });
      setSubmission(null);
      setError('submissionDeleted');
      setShowToast(true);
    } catch (err) {
      setError(err.message || 'genericError');
      setShowToast(true);
    } finally {
      setDeleting(false);
    }
  };

  const earned = submission && submission.is_approved;
  const canDelete = !!submission && sessionStorage.getItem('otherTeamId') === userInfo?.team_id;

  return (
    <Base activeNav="home">
      <Card>
        <Back to="/missions" />

        {mission && (
          <>
            <Center>
              <Image
                className="mb-3 circle"
                size="12rem"
                url={mission.photo_url || fallbackMissionImage}
              />
            </Center>

            <Title my="0" className="mb-2">
              {mission?.mission_name || t('mission')}
            </Title>

            <div className="d-flex justify-content-center align-items-center gap-2 mb-3" style={{ flexWrap: 'wrap' }}>
              <StatusPill submission={submission} />
              <span className={`points-chip ${earned ? 'points-chip--earned' : ''}`} style={{ marginTop: 0 }}>
                <Icon name="star" />
                {earned ? '+' : ''}{mission?.points || 0} {t('pts')}
              </span>
            </div>

            <div className="mb-3">{mission.description}</div>

            <hr />

            {!submission ? (
              <>
                <div className="mission-upload-hint mb-3">
                  <Icon name="camera" theme="primary" className="me-2" />
                  <span>{t('missionUploadHint')}</span>
                </div>
                <Button onClick={() => cameraInputRef.current.click()} styles={{}} theme="secondary">
                  <Icon name="camera" theme="light" className="me-1" /> {t('openCamera')}
                </Button>
                <Button onClick={() => fileInputRef.current.click()} styles={{}}>
                  <Icon name="photo-video" theme="light" className="me-1" /> {t('uploadFromGallery')}
                </Button>

                <input
                  type="file"
                  accept="image/*"
                  capture="environment"
                  ref={cameraInputRef}
                  style={{ display: 'none' }}
                  onChange={uploadFile}
                />
                <input
                  type="file"
                  accept="image/*"
                  ref={fileInputRef}
                  style={{ display: 'none' }}
                  onChange={uploadFile}
                />
              </>
            ) : (
              <>
                <Title my="0" className="mb-2">
                  <Icon name="camera" theme="primary" style={{ fontSize: '1.4rem' }} className="me-2" />
                  {t('yourSubmission')}
                </Title>

                <Center>
                  <div
                    className="mission-submission-image"
                    onClick={() => setSelectedImage(submission.image_url)}
                  >
                    <Image className="mb-2" size="15rem" url={submission.image_url} />
                    <span className="mission-submission-image__overlay">
                      <Icon name="zoom-in" theme="light" />
                    </span>
                  </div>
                </Center>

                <Modal
                  show={!!selectedImage}
                  title={t('photoPreview')}
                  onClose={() => setSelectedImage(null)}
                  hideFooter={true}
                >
                  {selectedImage && (
                    <div className="text-center">
                      <img
                        src={selectedImage}
                        alt="Preview"
                        className="img-fluid mb-3"
                        style={{ maxHeight: '60vh', borderRadius: '8px' }}
                      />
                      <div className="d-flex justify-content-center flex-wrap gap-2">
                        {shareable && (
                          <Button styles={{}} disabled={savingPhoto} onClick={() => savePhoto(sharePhoto)}>
                            <Icon name="share" theme="light" className="me-1" /> {t('share')}
                          </Button>
                        )}
                        <Button styles={{}} disabled={savingPhoto} onClick={() => savePhoto(downloadPhoto)}>
                          <Icon name="download" theme="light" className="me-1" /> {t('download')}
                        </Button>
                      </div>
                    </div>
                  )}
                </Modal>

                <div className="mt-3">
                  <div className="text-secondary mb-1" style={{ fontSize: '0.85rem' }}>{t('uploadedBy')}</div>
                  <div style={{ maxWidth: '20rem', margin: '0 auto' }}>
                    <Card to={`/u/${submission.uploaded_by_id}`}>
                      <SameLine className="row">
                        <div className="col-4">
                          <Image url={submission.uploaded_by_photo_url} size="4rem" className="circle" />
                        </div>
                        <div className="col-8 text-start">
                          <div>{submission.uploaded_by_name}</div>
                          {submission.uploaded_at && (
                            <div className="text-secondary" style={{ fontSize: '0.85rem' }}>
                              {new Date(submission.uploaded_at).toLocaleString(undefined, {
                                dateStyle: 'medium',
                                timeStyle: 'short',
                              })}
                            </div>
                          )}
                        </div>
                      </SameLine>
                    </Card>
                  </div>
                </div>
              </>
            )}

            {canDelete && (
              <div className="mt-3">
                <Button
                  theme="danger"
                  onClick={deleteSubmission}
                  disabled={deleting}
                  styles={{ margin: '0 auto' }}
                >
                  <Icon name="trash" theme="light" className="me-2" />
                  {deleting ? t('deleting') : t('deleteSubmission')}
                </Button>
              </div>
            )}
          </>
        )}

        <Toast showToast={showToast} setShowToast={setShowToast} message={t(error)} />
      </Card>
    </Base>
  );
};

export default MissionPage;
